import { hasArrayElements } from "@/apiUtils/commonUtils";
import { lensConfigurationSteps } from "@/apiUtils/constants";
import { endPageTransition, startPageTransition } from "@/apiUtils/pageLoadingUtils";
import IconInfo from "@/assets/icons/info.svg?react";
import {
  AdditionalOptions,
  AdditionalOptionsData,
  AdditionalTransitionOptions,
  ConfiguratorStepProps,
} from "@/common/lib/types";
import { getLangFromLocale } from "@/utils/common";
import { getPreviousStep } from "@/utils/lensConfigurator";
import { useMemo, useState } from "react";
import Navigation from "../../components/Navigation";
import { useConfiguratorContext } from "../../context/Provider";
import { lensAdditionalOptions } from "../../resource/config";
import LensInfoOverlay from "../lensCustomize/LensInfoOverlay";
import ProtectionFeatureCard from "./ProtectionFeatureCard";
import SummaryReview from "../../summaryReview/SummaryReview";

interface IProps extends ConfiguratorStepProps {
  stepOptions: AdditionalOptions;
}

const AdditionalFeatures = ({
  locale,
  stepOptions,
  currencyCode,
  translations,
  handleStepBack,
  handleStepData,
  handleAddToCart,
  handleJumpToStep,
}: IProps) => {
  const { data: options } = stepOptions;
  const lang = getLangFromLocale(locale);

  console.log("Additional Features stepOptions", { stepOptions });

  const { loading, steps, lensType } = useConfiguratorContext();
  const currentStep = lensConfigurationSteps.ADDITIONAL_OPTIONS;

  const defaultTransitionChoice = useMemo(() => {
    return options?.find((option) => option.coatingType === lensAdditionalOptions.TRANSITION)
      ?.transitionOptions?.[0];
  }, [options]);

  const [openLensQualitiesModal, setOpenLensQualitiesModal] = useState(false);
  const [transitionChoice, setTransitionChoice] = useState<AdditionalTransitionOptions | undefined>(
    defaultTransitionChoice,
  );

  const handleBack = async () => {
    if (typeof handleJumpToStep !== "function") return;
    startPageTransition(false);
    await handleJumpToStep(getPreviousStep(currentStep, steps));
    endPageTransition();
  };

  const handleToggleQualitiesModal = () => {
    setOpenLensQualitiesModal((prev) => !prev);
  };

  const handleTransitionSelect = (item: AdditionalTransitionOptions) => {
    setTransitionChoice(item);
  };

  const handleTransitionChoice = async (item: AdditionalOptionsData) => {
    await handleStepData(currentStep, { ...item, ...transitionChoice });
  };

  return (
    <div className="mt-2">
      <div className="flex items-center gap-4">
        <Navigation disabled={loading} className="hidden lg:flex px-4" onGoBack={handleBack}>
          {translations?.lensConfigurator?.lens_config_additional_options_section_title}
        </Navigation>
        <div className="flex items-center gap-2 lg:-mt-4">
          <p className="text-sm">
            {translations?.lensConfigurator?.lens_config_lens_qualities_badge_label}
          </p>
          <button type="button" onClick={handleToggleQualitiesModal}>
            <IconInfo className="h-4 w-4" />
          </button>
        </div>
      </div>
      <p className="mb-7 mt-2 text-[0.75rem] lg:mt-0">
        <span className="mr-1 font-bold">
          {
            translations?.lensConfigurator
              ?.lens_config_additional_options_section_subtitle_bold_text
          }
        </span>
        <span>
          {translations?.lensConfigurator?.lens_config_additional_options_section_subtitle}
        </span>
      </p>
      <section className="flex flex-col gap-3">
        {hasArrayElements(options) &&
          options.map((data, i) => (
            <ProtectionFeatureCard
              key={i}
              currencyCode={currencyCode}
              handleStepData={handleStepData}
              handleTransitionChoice={handleTransitionChoice}
              item={data}
              lang={lang}
              lensType={lensType}
              translations={translations}
              handleTransitionSelect={handleTransitionSelect}
              transitionChoice={transitionChoice}
            />
          ))}
      </section>
      <SummaryReview currencyCode={currencyCode} locale={locale} translations={translations} />

      {openLensQualitiesModal && (
        <LensInfoOverlay
          open={openLensQualitiesModal}
          lensInfo={stepOptions.lensInfo}
          onClose={handleToggleQualitiesModal}
        />
      )}
    </div>
  );
};

export default AdditionalFeatures;
